import { sectionType } from "../../../sectionType";
import { ServiceManager } from "../../../services/service-manager";
import { alignment } from "../models/alignment.enum";
import { bodiesEnum } from "./models/bodies.enum";
import { columnType } from "./models/column-type.enum";
import { coreType } from "./models/core-type.enum";
import { coreTypesModel } from "./models/core-types.model";
import { crownType } from "./models/crown-type.enum";
import { headType } from "./models/head-type.enum";
import { headTypesModel } from "./models/head-types.model";
import { middleType } from "./models/middle-type.enum";
import { moosPlazaTypesModel } from "./models/moos-plaza-types.model";
import { plinthType } from "./models/plinth-type.enum";
import { TowerService } from "./tower.service";
import { TowerColumn, TowerPart } from "./models/props-config";
import { segmentType } from "./models/segment-type";
import { columnDefs, subtypeEnum, variantEnum } from "./models/column-definition";
import { UnitAmount } from "../models/column";
import { partTypeEnum } from "./models/part-type.enum";
import { ColumnBaseService } from "../column-base.service";
import { unitBaseTypeEnum } from "../../../unit-base-type-enum";

export interface Config {
    selectedPlinthType: plinthType,
    selectedMiddleType: middleType,
    selectedCrownType: crownType,
    plinthFloors: number,
    middleFloors: number,
    crownFloors: number,
    selectedMoosPlazaType: columnType,
    moosPlazaPosition: bodiesEnum,
    selectedMoosPlazaAlignment: alignment,
    selectedHeadType: headType,
    selectedCoreType: coreType,
    customPlinthWidth: number,
    customPlinthHeight: number,
}

export class ColumnService extends ColumnBaseService {

    getTotalAmountOfAllUnits(parts: TowerPart[]): UnitAmount[] {
        let unitAmounts: UnitAmount[] = [];
        if (parts) {
            unitAmounts = this.computeTotalAmountsForParts(parts);
            const totalUnits = this.totalUnits(unitAmounts);

            unitAmounts.forEach(unitAmount => {
                unitAmount.percentage = unitAmount.amount / totalUnits * 100;
            });
        }
        return unitAmounts;
    }

    createPossibleColumnDefinitions(
        c: Config
    ): TowerPart[] {

        const headLeftPart: TowerPart = {
            type: partTypeEnum.headLeft,
            columns: [],
        }

        const headRightPart: TowerPart = {
            type: partTypeEnum.headRight,
            columns: [],
        }

        const corePart: TowerPart = {
            type: partTypeEnum.core,
            columns: [],
        }

        const bodyAPart: TowerPart = {
            type: partTypeEnum.bodyA,
            columns: [],
        }

        const bodyBPart: TowerPart = {
            type: partTypeEnum.bodyB,
            columns: [],
        }

        const ch = c.customPlinthHeight;
        const cw = c.customPlinthWidth;

        if (this.validateMoosPlazaIsAmongAvailable(c.selectedHeadType, c.selectedCoreType, c.selectedMoosPlazaType)) {

            // Heads
            const headLeft = this.createHeadColumn(alignment.left, c.selectedHeadType, c.selectedPlinthType, c.plinthFloors, c.middleFloors, c.crownFloors, c.customPlinthHeight, c.customPlinthWidth);
            headLeftPart.columns.push(headLeft);

            // Moos plein
            if (c.selectedMoosPlazaType !== columnType.none) {
                let col = this.createColumnFromDefinition(c.selectedMoosPlazaType as any, c.selectedPlinthType, subtypeEnum.single, subtypeEnum.single, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                if (c.moosPlazaPosition === bodiesEnum.A) {
                    bodyAPart.columns.push(col);
                } else {
                    bodyBPart.columns.push(col);
                }
            }

            const coreColumn = this.createCoreColumn(c.selectedCoreType, c.selectedPlinthType, c.plinthFloors, c.middleFloors, c.crownFloors, c.customPlinthHeight, c.customPlinthWidth);
            corePart.columns.push(coreColumn);

            this.fillReplacements(c.moosPlazaPosition, c.selectedMoosPlazaType, bodyAPart.columns, bodyBPart.columns, c.selectedHeadType, c.selectedCoreType, c.selectedPlinthType, c.plinthFloors, c.middleFloors, c.crownFloors, c.customPlinthHeight, c.customPlinthWidth);

            // Last column is right head type if it exists
            const headRightColumn = this.createHeadColumn(alignment.right, c.selectedHeadType, c.selectedPlinthType, c.plinthFloors, c.middleFloors, c.crownFloors, c.customPlinthHeight, c.customPlinthWidth)
            headRightPart.columns.push(headRightColumn);

        }
        return [headLeftPart, bodyAPart, bodyBPart, corePart, headRightPart];
    }

    validateMoosPlazaIsAmongAvailable(selectedHeadType: headType, selectedCoreType: coreType, selectedMoosPlazaType: columnType) {
        const availableMoosPlazaTypes = TowerService.getAvailableMoosPlazaTypes(selectedHeadType, selectedCoreType);
        return TowerService.checkIfMoosPlazaIsAmongAvailable(availableMoosPlazaTypes, selectedMoosPlazaType);
    }

    // This method is super manual, but solves the problem for now, it will need to become more intelligent
    private fillReplacements(
        moosPlazaPosition: bodiesEnum,
        selectedMoosPlazaType: columnType,
        bodyAColumns: TowerColumn[],
        bodyBColumns: TowerColumn[],
        selectedHeadType: headType,
        selectedCoreType: coreType,
        selectedPlinthType: plinthType,
        plinthFloors: number,
        middleFloors: number,
        crownFloors: number,
        customPlinthHeight: number,
        customPlinthWidth: number,
    ) {


        const ch = customPlinthHeight;
        const cw = customPlinthWidth;

        let head = headTypesModel.find(s => s.type === selectedHeadType);
        let coreCorridor = coreTypesModel.find(s => s.type === selectedCoreType);

        const baysOnBody = coreCorridor.bays - head.coreDiffBays;
        const baysMoosPlaza = moosPlazaTypesModel.find(mp => mp.type === selectedMoosPlazaType).bays;

        const baysToFillBodyA = moosPlazaPosition === bodiesEnum.A ? baysOnBody - baysMoosPlaza : baysOnBody;
        const baysToFillBodyB = moosPlazaPosition === bodiesEnum.B ? baysOnBody - baysMoosPlaza : baysOnBody;

        let remainderA = [];
        let remainderB = [];
        // options for knapsack to choose from. This is reasonably long array of items with repeating names to make knapsack solution usable in our case
        var items2 = [
            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cxl" },

            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cxl" },

            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cxl" },

            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cxl" },

            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cxl" },

            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cxl" },

            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cxl" },
        ];

        const resA = ServiceManager.utilityService.knapsack(items2, baysToFillBodyA);
        const resB = ServiceManager.utilityService.knapsack(items2, baysToFillBodyB);

        resA.solution.forEach(obj => {
            if (obj.name === 'cxs') {
                const col = this.createColumnFromDefinition(columnType.C_XS, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variantEnum.single, ch, cw);
                remainderA.push(col);
            } else if (obj.name === 'cm') {
                const col = this.createColumnFromDefinition(columnType.C_M, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variantEnum.single, ch, cw);
                remainderA.push(col);
            } else if (obj.name === 'cs') {
                const col = this.createColumnFromDefinition(columnType.C_S1S2, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variantEnum.single, ch, cw);
                remainderA.push(col);
            } else if (obj.name === 'cxl') {
                const col = this.createColumnFromDefinition(columnType.C_XL, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variantEnum.single, ch, cw);
                remainderA.push(col);
            }
        });

        resB.solution.forEach(obj => {
            if (obj.name === 'cxs') {
                const col = this.createColumnFromDefinition(columnType.C_XS, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variantEnum.single, ch, cw);
                remainderB.push(col);
            } else if (obj.name === 'cm') {
                const col = this.createColumnFromDefinition(columnType.C_M, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variantEnum.single, ch, cw);
                remainderB.push(col);
            } else if (obj.name === 'cs') {
                const col = this.createColumnFromDefinition(columnType.C_S1S2, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variantEnum.single, ch, cw);
                remainderB.push(col);
            } else if (obj.name === 'cxl') {
                const col = this.createColumnFromDefinition(columnType.C_XL, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variantEnum.single, ch, cw);
                remainderB.push(col);
            }
        });

        bodyBColumns.push(...remainderB);
        bodyAColumns.push(...remainderA);
    }

    private createCoreColumn(selectedCoreType: coreType, selectedPlinthType: plinthType, plinthFloors, middleFloors: number, crownFloors: number, customPlinthHeight: number, customPlinthWidth: number) {
        let column;
        switch (selectedCoreType) {
            case coreType.O_X4:
                column = this.createColumnFromDefinition(columnType.O_X4, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variantEnum.single, customPlinthHeight, customPlinthWidth);
                break;
            case coreType.O_X5:
                column = this.createColumnFromDefinition(columnType.O_X5, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variantEnum.single, customPlinthHeight, customPlinthWidth);
                break;
            case coreType.O_X6:
                column = this.createColumnFromDefinition(columnType.O_X6, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variantEnum.single, customPlinthHeight, customPlinthWidth);
                break;
            default:
                break;
        }
        return column;
    }

    private createHeadColumn(
        headAlignment: alignment,
        selectedHeadType: headType,
        selectedPlinthType: plinthType,
        plinthFloors: number,
        middleFloors: number,
        crownFloors: number,
        customPlinthHeight: number,
        customPlinthWidth: number,
    ) {
        let variant;
        if (headAlignment === alignment.left) {
            variant = variantEnum.left
        } else {
            variant = variantEnum.right
        }
        let result = this.createColumnFromDefinition(selectedHeadType, selectedPlinthType, subtypeEnum.single, subtypeEnum.single, plinthFloors, middleFloors, crownFloors, variant, customPlinthHeight, customPlinthWidth);
        return result;
    }

    private totalUnits(units: UnitAmount[]) {
        return units.map(u => u.amount).reduce((p, c) => p + c, 0);
    }

    createColumnFromDefinition(type: columnType | headType, plinth: plinthType | subtypeEnum, middle: middleType | subtypeEnum, crown: crownType | subtypeEnum, plinthFloors: number, middleFloors: number, crownFloors: number, variant: variantEnum, customPlinthHeight: number, customPlinthWidth: number): TowerColumn {
        if (type !== columnType.none) {
            const columnDefinition = columnDefs.find(s => s.type === type);

            const bays = columnDefinition.bays;

            let plinthSections: sectionType[] = [sectionType.none];

            let amountOfUnits = [];

            if (![headType.H_2C_XL, headType.H_2C_S1S2, headType.H_C_S1S2, columnType.O_X4, columnType.O_X5, columnType.O_X6].includes(type as headType)) {
                middle = subtypeEnum.single;
                crown = subtypeEnum.single;
            }

            if (plinth !== plinthType.Custom) {
                const plinthSubtype = subtypeEnum.single;
                const plinthDef = columnDefinition.segmentDefinitions.find(s => s.type === segmentType.plinth && s.subtype === plinthSubtype);
                const plinthFloorDef = plinthDef.floorVariants.find(s => s.floors === plinthFloors);
                const plinthSideDef = plinthFloorDef.variants.find(f => f.variant === variant);

                plinthSections = plinthSideDef.sections;
            }

            let middleSections: sectionType[];

            const middleDef = columnDefinition.segmentDefinitions.find(s => s.type === segmentType.middle && s.subtype === middle);
            const middleFloorDef = middleDef.floorVariants.find(s => s.floors === 1);

            const middleSideDef = middleFloorDef.variants.find(f => f.variant === variant);
            middleSections = middleSideDef.sections;

            let crownSections: sectionType[];

            const crownDef = columnDefinition.segmentDefinitions.find(s => s.type === segmentType.crown && s.subtype === crown);
            const crownFloorDef = crownDef.floorVariants.find(s => s.floors === crownFloors);

            const crownVariantDef = crownFloorDef.variants.find(f => f.variant === variant);
            crownSections = crownVariantDef.sections;

            let result: TowerColumn = {
                id: Math.random().toString(),
                type,
                bays,
                segments: [
                    {
                        type: segmentType.plinth,
                        subtype: plinth as subtypeEnum,
                        custom: plinth === plinthType.Custom,
                        sectionTypes: plinthSections,
                        floorsInSegment: plinthFloors,
                        customSegmentHeight: (plinth === plinthType.Custom ? customPlinthHeight : 0),
                        customSegmentWidth: (plinth === plinthType.Custom ? customPlinthWidth : 0)
                    },
                    {
                        type: segmentType.middle,
                        subtype: middle as subtypeEnum,
                        sectionTypes: middleSections,
                        floorsInSegment: middleFloors,
                    },
                    {
                        type: segmentType.crown,
                        subtype: crown as subtypeEnum,
                        sectionTypes: crownSections,
                        floorsInSegment: crownFloors,
                    }
                ],
                amountOfUnits,
            }

            return result;
        }
    }
}